/**
 * Part → look: which kit materials a part's faces, long edges and ends use. Reads only the
 * part's own text (stockId, material, face, edge) and thickness, so it works for any family.
 * Pass `resolveLook` to `assembleKit` to override it (for example from an appearance profile).
 */
import type { KitPart, PartLook, Species } from './types';

const PLY_EDGES: Array<[number, string]> = [[18, 'ply-edge-18'], [12, 'ply-edge-12'], [6, 'ply-edge-6']];

export function plyEdgeFor(thicknessMm: number): string {
  return PLY_EDGES.reduce((best, cur) => (Math.abs(cur[0] - thicknessMm) < Math.abs(best[0] - thicknessMm) ? cur : best))[1];
}

const lin = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
const BIRCH_SRGB: [number, number, number] = [0.875, 0.765, 0.585];
/** Stain targets, sRGB: near the graded species means (pipeline/blender/lib/materials.py FACES), a
 * touch browner, as a stain on birch reads. */
const SPECIES_SRGB: Record<'sheesham' | 'oak', [number, number, number]> = { sheesham: [0.6, 0.355, 0.19], oak: [0.735, 0.585, 0.415] };

/** Linear multiplier that stains the birch ply face to a species' mean colour. */
export function stainFor(species: 'sheesham' | 'oak'): [number, number, number] {
  const t = SPECIES_SRGB[species];
  return [0, 1, 2].map((i) => lin(t[i]) / lin(BIRCH_SRGB[i])) as [number, number, number];
}

export function speciesOf(text: string): 'sheesham' | 'oak' | 'birch' {
  const t = text.toLowerCase();
  if (/sheesham|rosewood|sissoo/.test(t)) return 'sheesham';
  if (/\boak\b|oak-/.test(t)) return 'oak';
  return 'birch';
}

export function resolvePartLook(part: KitPart, opts: { edgeProfile?: string } = {}): PartLook {
  const text = [part.stockId, part.material, part.face].filter(Boolean).join(' ');
  const isSheet = /ply/i.test(part.material) || (!/board/i.test(part.stockId ?? '') && /ply/i.test(text));
  const sp = speciesOf(`${part.stockId ?? ''} ${part.material}`);
  const tMm = part.thicknessTicks / 10;
  const lipping = /lipp/i.test(part.edge ?? '');
  const edgeText = part.edge ?? '';
  // thick solid stock (38-45 mm frames) gets a heavier arris than 18-25 mm boards
  const thick = tMm >= 35;
  const edgeProfile = opts.edgeProfile ?? (thick
    ? (/chamfer/i.test(edgeText) ? 'chamfer-3' : /round/i.test(edgeText) ? 'roundover-3' : 'eased-thick')
    : (/round/i.test(edgeText) ? 'roundover-2' : 'square'));
  if (isSheet) {
    const edge = plyEdgeFor(tMm);
    if (sp !== 'birch' && /stain/i.test(part.material)) {
      // birch ply stained to match the solid timber
      return { species: 'birch-ply', face: 'ply-face', edge, end: edge, edgeUv: 'normalised', edgeProfile: 'square', lipping, tint: stainFor(sp) };
    }
    const face = sp === 'birch' ? 'ply-face' : `solid-face-${sp}`;
    return { species: sp === 'birch' ? 'birch-ply' : (sp as Species), face, edge, end: edge, edgeUv: 'normalised', edgeProfile: 'square', lipping };
  }
  if (sp === 'birch') {
    return { species: 'birch-ply', face: 'ply-face', edge: 'ply-face', end: 'end-grain-oak', edgeUv: 'metres', edgeProfile, lipping };
  }
  return { species: sp, face: `solid-face-${sp}`, edge: `solid-edge-${sp}`, end: `end-grain-${sp}`, edgeUv: 'metres', edgeProfile, lipping };
}

/** Kit material ids a set of looks needs (plus the metals and slots every piece may use). */
export function materialsFor(looks: Iterable<PartLook>): string[] {
  const ids = new Set<string>(['steel', 'brass', 'black', 'slot.shadow', 'slot.dowel']);
  for (const l of looks) {
    ids.add(l.face);
    ids.add(l.edge);
    ids.add(l.end);
    if (l.lipping) {
      ids.add(`solid-face-${l.species === 'birch-ply' ? 'oak' : l.species}`);
      ids.add(`end-grain-${l.species === 'birch-ply' ? 'oak' : l.species}`);
    }
  }
  return [...ids];
}
